import React, { useState } from "react";
import { Mail, MapPin, Instagram, Send, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, VILLA } from "../lib/api";
import { useLanguage } from "../lib/i18n";

const Contact = () => {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                message: form.message,
            });
            toast.success(t("form.success"));
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || t("form.error")
            );
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full bg-transparent border-b border-border py-3 focus:outline-none focus:border-primary transition-colors text-foreground placeholder:text-muted-foreground/60 font-body rounded-none";

    return (
        <div data-testid="contact-page" className="pt-32 pb-24 bg-background">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16">
                <div>
                    <div className="overline text-secondary mb-4">{t("page.contact.overline")}</div>
                    <h1 className="font-display text-5xl sm:text-6xl font-light text-primary leading-[0.95] tracking-tight">
                        {t("page.contact.title")}
                    </h1>
                    <p className="mt-6 text-muted-foreground font-light leading-relaxed max-w-md">
                        {t("page.contact.intro")}
                    </p>
                    <div className="mt-12 space-y-6">
                        <a
                            href={`mailto:${VILLA.email}`}
                            data-testid="contact-email-block"
                            className="flex items-start gap-4 group"
                        >
                            <Mail
                                size={18}
                                strokeWidth={1.25}
                                className="text-secondary mt-1 shrink-0 group-hover:scale-110 transition-transform"
                            />
                            <div>
                                <div className="overline text-muted-foreground">
                                    {t("label.email")}
                                </div>
                                <div
                                    data-testid="contact-email-link"
                                    className="font-display text-xl text-primary group-hover:text-secondary transition-colors"
                                >
                                    {VILLA.email}
                                </div>
                            </div>
                        </a>
                        <a
                            href="https://www.google.com/maps/search/?api=1&query=Villa+Alam+Lembongan"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-address-block"
                            className="flex items-start gap-4 group"
                        >
                            <MapPin
                                size={18}
                                strokeWidth={1.25}
                                className="text-secondary mt-1 shrink-0 group-hover:scale-110 transition-transform"
                            />
                            <div>
                                <div className="overline text-muted-foreground">
                                    {t("label.location")}
                                </div>
                                <div
                                    data-testid="contact-address-link"
                                    className="font-display text-xl text-primary group-hover:text-secondary transition-colors leading-snug"
                                >
                                    8CCX+PWG, Jl. Jungutbatu, Jungutbatu,<br />
                                    Kec. Nusa Penida, Kabupaten Klungkung,<br />
                                    Bali 80771, Indonesia
                                </div>
                            </div>
                        </a>
                        <a
                            href="https://instagram.com/villa_alam_lembongan"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-instagram-block"
                            className="flex items-start gap-4 group"
                        >
                            <Instagram
                                size={18}
                                strokeWidth={1.25}
                                className="text-secondary mt-1 shrink-0 group-hover:scale-110 transition-transform"
                            />
                            <div>
                                <div className="overline text-muted-foreground">
                                    {t("label.instagram")}
                                </div>
                                <div className="font-display text-xl text-primary group-hover:text-secondary transition-colors">
                                    @villa_alam_lembongan
                                </div>
                            </div> 
                        </a>
                        <a
                            href="https://www.booking.com/hotel/id/villa-alam-klungkung-regency.en-gb.html"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-booking-block"
                            className="flex items-start gap-4 group"
                        >
                            <BookOpen
                                size={18}
                                strokeWidth={1.25}
                                className="text-secondary mt-1 shrink-0 group-hover:scale-110 transition-transform"
                            />
                            <div>
                                <div className="overline text-muted-foreground">
                                    {t("label.booking")}
                                </div>
                                <div className="font-display text-xl text-primary group-hover:text-secondary transition-colors">
                                    Booking.com: Villa Alam Lembongan
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
                <form
                    data-testid="contact-form"
                    onSubmit={submit}
                    className="space-y-8"
                >
                    <div>
                        <div className="overline text-muted-foreground mb-2 text-[10px]">
                            {t("form.name")}
                        </div>
                        <input
                            data-testid="contact-name"
                            required
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <div className="overline text-muted-foreground mb-2 text-[10px]">
                            {t("form.email")}
                        </div>
                        <input
                            data-testid="contact-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) =>
                                setForm({ ...form, email: e.target.value })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <div className="overline text-muted-foreground mb-2 text-[10px]">
                            {t("form.phone")}
                        </div>
                        <input
                            data-testid="contact-phone"
                            type="tel"
                            value={form.phone}
                            onChange={(e) =>
                                setForm({ ...form, phone: e.target.value })
                            }
                            placeholder="+61 …"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <div className="overline text-muted-foreground mb-2 text-[10px]">
                            {t("form.message")}
                        </div>
                        <textarea
                            data-testid="contact-message"
                            required
                            rows={6}
                            value={form.message}
                            onChange={(e) =>
                                setForm({ ...form, message: e.target.value })
                            }
                            className={`${inputCls} resize-none`}
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="contact-submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-3 px-12 py-4 bg-primary text-white text-xs tracking-[0.2em] uppercase hover:bg-secondary transition-colors disabled:opacity-60"
                    >
                        {submitting ? t("form.sending") : t("form.send")}
                        <Send size={14} />
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Contact;
